import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, cfoSnapshot, cfoReadiness, cfoGapReport, cfoAnswers, cfoBrief, cfoTimeSummary, cfoForecast, cfoSourceHealth, formatApiError } from "@/lib/api";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const hz = (s) => (s ? String(s).replace(/_/g, " ") : s);

const STATE_TONE = {
  AVAILABLE: "#8fd19e", DERIVED_FROM_LEDGER: "#8fd19e", CURRENT: "#8fd19e",
  SOURCE_NOT_CONNECTED: "#e7a877", DATA_NOT_AVAILABLE: "#e7a877", DATA_INSUFFICIENT: "#e7a877",
  STALE: "#e7a877", UNKNOWN: "#c8a24a",
};
const Chip = ({ v, testid }) => (
  <span data-testid={testid} className="text-[10px] px-2 py-0.5 rounded-[6px] font-semibold tracking-wide"
    style={{ background: "rgba(180,139,63,0.12)", color: STATE_TONE[v] || "#d8c9a6", border: "1px solid rgba(180,139,63,0.25)" }}>
    {hz(v)}
  </span>
);
const Card = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-[10px] border border-[rgba(180,139,63,0.18)] bg-[rgba(20,18,14,0.55)] p-5">
    <h3 className="text-[13px] uppercase tracking-[0.14em] text-[#c8a24a] mb-3">{title}</h3>
    {children}
  </div>
);
const Row = ({ k, v, chip, testid }) => (
  <div className="flex items-center justify-between py-1.5 border-t border-[rgba(180,139,63,0.1)] first:border-0">
    <span className="text-[13px] text-[var(--muted)]">{k}</span>
    <span className="text-[13px] font-medium flex items-center gap-2" data-testid={testid}>{v}{chip && <Chip v={chip} />}</span>
  </div>
);

function Console({ onSignOut }) {
  const [snap, setSnap] = useState(null);
  const [ready, setReady] = useState(null);
  const [gap, setGap] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [brief, setBrief] = useState(null);
  const [timeSum, setTimeSum] = useState(null);
  const [fc, setFc] = useState(null);
  const [src, setSrc] = useState(null);
  const [tab, setTab] = useState("control");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [s, r, g, a, b, ts, f, sh] = await Promise.all([cfoSnapshot(), cfoReadiness(), cfoGapReport(), cfoAnswers(), cfoBrief("weekly"), cfoTimeSummary(), cfoForecast(), cfoSourceHealth()]);
      setSnap(s); setReady(r); setGap(g); setAnswers(a); setBrief(b); setTimeSum(ts); setFc(f); setSrc(sh);
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail, "CFO data is temporarily unavailable. Please try again.")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabs = [["control", "Control Center"], ["ops", "Time · Forecast · Sources"], ["portfolio", "Entity Portfolio"], ["answers", "CFO Answers"], ["gap", "Integration Gaps"], ["readiness", "Readiness"]];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)] px-6 py-8" data-testid="cfo-console">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold" style={{ fontFamily: "Cinzel, serif" }}>JMH — CFO Operating System</h1>
            <p className="text-[13px] text-[var(--muted)] mt-1">
              Consolidated financial authority · env <b>{snap?.environment}</b>
              {snap?.entity_basis && <> · entity basis <Chip v={snap.entity_basis} testid="cfo-entity-basis" /></>}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/founder/hours" data-testid="cfo-nav-hours" className="text-[12px] text-[#c8a24a] hover:underline">Weekly Hours →</a>
            <button onClick={onSignOut} data-testid="cfo-signout" className="text-[12px] text-[var(--muted)] hover:text-[#c8a24a]">Sign out</button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="cfo-tabs">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`cfo-tab-${id}`} onClick={() => setTab(id)}
              className="text-[12px] px-3 py-1.5 rounded-[8px] border"
              style={{ borderColor: "rgba(180,139,63,0.3)", background: tab === id ? "rgba(180,139,63,0.18)" : "transparent", color: tab === id ? "#e6d8b0" : "var(--muted)" }}>
              {label}
            </button>
          ))}
        </div>

        {err && <div className="mb-4"><p role="alert" className="text-[#e7a877] text-sm" data-testid="cfo-error">{err}</p><button type="button" className="jmh-cta mt-3" onClick={load}>Try again</button></div>}
        {!snap && !err && <p className="text-[var(--muted)] text-sm">Loading…</p>}

        {tab === "control" && snap && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="cfo-control">
            <Card title="Cash" testid="cfo-cash">
              <Row k="Book cash (ledger)" v={money(snap.cash.book_cash_consolidated)} testid="cfo-book-cash" />
              <Row k="Freshness" v="" chip={snap.cash.book_cash_freshness} />
              <Row k="Live bank feed" v="" chip={snap.cash.live_bank_cash_state} testid="cfo-live-bank" />
              <Row k="Restricted" v={hz(snap.cash.restricted_cash)} />
            </Card>
            <Card title="Runway" testid="cfo-runway">
              <Row k="State" v="" chip={snap.runway.state} />
              <Row k="Runway (months)" v={snap.runway.runway_months ?? "—"} testid="cfo-runway-months" />
              <Row k="Monthly burn (avg)" v={money(snap.runway.monthly_burn_avg)} />
            </Card>
            <Card title="Profitability (derived)" testid="cfo-profit">
              <Row k="Revenue" v={money(snap.profitability.consolidated_revenue)} />
              <Row k="Expense" v={money(snap.profitability.consolidated_expense)} />
              <Row k="Net contribution" v={money(snap.profitability.consolidated_net)} testid="cfo-net" />
            </Card>
            <Card title="Receivables / Payables" testid="cfo-arap">
              <Row k="AR outstanding" v={money(snap.receivables.total_outstanding)} chip={snap.receivables.state} />
              <Row k="AR overdue" v={String((snap.receivables.overdue || []).length)} />
              <Row k="Payables" v="" chip={snap.payables.state} testid="cfo-ap-state" />
            </Card>
            <Card title="Intercompany" testid="cfo-ic">
              <Row k="Ledger IC net" v={money(snap.intercompany.ledger_intercompany_net_sum)} />
              <Row k="Reconciliation" v="" chip={snap.intercompany.reconciliation} />
            </Card>
            <Card title="Founder Time & Tax" testid="cfo-time-tax">
              <Row k="Founder time" v="" chip={snap.founder_time.state} testid="cfo-time-state" />
              <Row k="Automatic?" v={snap.founder_time.automatic_today ? "Yes" : "No"} />
              <Row k="Tax intelligence" v="" chip={snap.tax.state} />
              <Row k="Tax authority" v={snap.tax.authority} />
            </Card>
          </div>
        )}

        {tab === "portfolio" && snap && (
          <Card title="Entity Portfolio (ledger-derived)" testid="cfo-portfolio">
            <div className="overflow-x-auto"><table className="w-full text-[13px]">
              <thead><tr className="text-[var(--muted)] text-left text-[11px] uppercase tracking-wide">
                <th className="py-2">Entity</th><th>Book Cash</th><th>Revenue</th><th>Expense</th><th>Net</th><th>Profitable</th></tr></thead>
              <tbody>
                {snap.profitability.by_entity.map((e, i) => (
                  <tr key={i} className="border-t border-[rgba(180,139,63,0.1)]" data-testid={`cfo-entity-${i}`}>
                    <td className="py-2">{e.entity_name}{e.is_synthetic && <span className="text-[10px] text-[#e7a877] ml-1">SYNTH</span>}</td>
                    <td>{money(e.book_cash)}</td><td>{money(e.revenue)}</td><td>{money(e.expense)}</td><td>{money(e.net_contribution)}</td>
                    <td>{e.profitable == null ? <Chip v="UNKNOWN" /> : (e.profitable ? "✓" : "✗")}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </Card>
        )}

        {tab === "answers" && answers && (
          <Card title="10 CFO Questions — answered from governed evidence" testid="cfo-answers">
            {Object.entries(answers).filter(([k]) => /^\d/.test(k)).map(([k, v]) => (
              <Row key={k} k={hz(k.replace(/^\d+_/, ""))} v={<code className="text-[11px] text-[#d8c9a6]">{JSON.stringify(v)}</code>} testid={`cfo-answer-${k.split("_")[0]}`} />
            ))}
          </Card>
        )}

        {tab === "gap" && gap && (
          <Card title="Financial Integration Gap Report" testid="cfo-gap">
            <p className="text-[12px] text-[var(--muted)] mb-3">{gap.principle}</p>
            {gap.gaps.map((g, i) => (
              <div key={i} className="py-2 border-t border-[rgba(180,139,63,0.1)]" data-testid={`cfo-gap-${i}`}>
                <div className="flex items-center gap-2"><b className="text-[13px]">{hz(g.source)}</b>
                  <span className="text-[11px] text-[var(--muted)]">— {g.business_need}</span></div>
                <div className="text-[11px] text-[var(--muted)] mt-1">Recommended: {g.recommended_category} · new software necessary: <b>{String(g.new_software_necessary)}</b> · buildable now: {g.buildable_now}</div>
              </div>
            ))}
          </Card>
        )}

        {tab === "ops" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="cfo-ops">
            <Card title="Founder Time (auto-captured)" testid="cfo-time-card">
              <Row k="State" v="" chip={timeSum?.state} testid="cfo-time-state2" />
              <Row k="Automatic" v={timeSum?.automatic ? "Yes" : "No"} testid="cfo-time-auto" />
              <Row k="Active hours" v={timeSum?.total_active_hours ?? "—"} testid="cfo-time-hours" />
              <Row k="Sessions" v={timeSum?.session_count ?? "—"} />
              <Row k="Unattributed (min)" v={timeSum?.unattributed_minutes ?? "—"} />
              <Row k="Economic value" v={hz(timeSum?.economic_value)} />
            </Card>
            <Card title="Cash Forecast · 30-day" testid="cfo-forecast-card">
              <Row k="State" v="" chip={fc?.["30"]?.state} />
              <Row k="Starting cash" v={money(fc?.["30"]?.starting_cash)} chip={fc?.["30"]?.cash_basis} />
              <Row k="Projected end" v={money(fc?.["30"]?.projected_end_cash)} testid="cfo-forecast-end" />
              <Row k="Confidence" v={hz(fc?.["30"]?.confidence)} />
              <Row k="Missing inputs" v={(fc?.["30"]?.missing_inputs || []).map(hz).join(", ") || "none"} testid="cfo-forecast-missing" />
            </Card>
            <Card title="Source Health (no false green)" testid="cfo-sources-card">
              {src && Object.entries(src).filter(([, v]) => v && typeof v === "object" && v.state).map(([k, v]) => (
                <Row key={k} k={hz(k)} v="" chip={v.state} testid={`cfo-src-${k}`} />
              ))}
            </Card>
          </div>
        )}

        {tab === "readiness" && ready && (
          <Card title="Whole-CFO Readiness" testid="cfo-readiness">
            <Row k="Status" v={ready.status} testid="cfo-status" />
            <Row k="Contradictions" v={ready.contradictions.length === 0 ? "none" : ready.contradictions.join(", ")} testid="cfo-contradictions" />
            <Row k="Entity write hold" v={ready.entity_write_hold} />
            <Row k="Money movement" v={ready.money_movement} />
            <Row k="FMBI" v={String(ready.fmbi)} />
            <div className="mt-3 grid grid-cols-2 gap-x-6">
              {Object.entries(ready.capabilities).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1 text-[12px]">
                  <span className="text-[var(--muted)]">{hz(k)}</span><Chip v={typeof v === "string" ? v.split(" ")[0] : v} /></div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await adminLogin(email, password); onIn(); }
    catch { setErr("Login failed."); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--fg)]">
      <form onSubmit={submit} className="w-full max-w-sm p-6 rounded-[10px] border border-[rgba(180,139,63,0.2)]" data-testid="cfo-login-form">
        <h1 className="text-xl mb-4" style={{ fontFamily: "Cinzel, serif" }}>JMH CFO — Sign in</h1>
        <input data-testid="cfo-login-email" className="w-full mb-3 px-3 py-2 rounded-[8px] bg-transparent border border-[rgba(180,139,63,0.3)]" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="cfo-login-password" type="password" className="w-full mb-3 px-3 py-2 rounded-[8px] bg-transparent border border-[rgba(180,139,63,0.3)]" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <p className="text-[#e88] text-sm mb-3">{err}</p>}
        <button data-testid="cfo-login-submit" className="w-full py-2 rounded-[8px] bg-[rgba(180,139,63,0.2)] border border-[rgba(180,139,63,0.4)]">Enter</button>
      </form>
    </div>
  );
}

export default function FounderCFO() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
